import { Route, RoutePoint, RouteStyle, ContainerPoint } from "../types";

/**
 * Сервис для работы с маршрутами
 * Создание/валидация/утилиты
 */
export class RouteService {
    static createRoute(
        id: string,
        name: string,
        points: [number, number][] | RoutePoint[],
        style?: RouteStyle
    ): Route {
        const routePoints: RoutePoint[] = points.map((point, index) => {
            if (Array.isArray(point)) {
                return { coords: point, order: index };
            }
            return { ...point, order: point.order ?? index };
        });

        // фиксируем порядок
        routePoints.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        return {
            id,
            name,
            points: routePoints,
            style: style || this.getDefaultStyle(),
            visible: true,
        };
    }

    static getDefaultStyle(): RouteStyle {
        return {
            strokeColor: "#1e98ff",
            strokeWidth: 8,
            strokeOpacity: 0.95,
            strokeStyle: "solid",
            zIndex: 100,
        };
    }

    static getStyleByIndex(index: number): RouteStyle {
        const colors = [
            "#1e98ff",
            "#ff6b35",
            "#4ecdc4",
            "#ffe66d",
            "#ff6b9d",
            "#95e1d3",
            "#f38181",
            "#a8e6cf",
        ];

        return {
            strokeColor: colors[index % colors.length],
            strokeWidth: 8,
            strokeOpacity: 0.95,
            strokeStyle: "solid",
            zIndex: 100 + index,
        };
    }

    static validateRoute(route: Route): boolean {
        return !!(route?.id && route?.name && route?.points && route.points.length >= 2);
    }

    static getRouteCoordinates(route: Route): [number, number][] {
        const sorted = [...route.points].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        return sorted.map((p) => p.coords);
    }

    static calculateRouteLengthKm(route: Route): number {
        const coords = this.getRouteCoordinates(route);
        if (coords.length < 2) return 0;

        let total = 0;
        for (let i = 0; i < coords.length - 1; i++) {
            const [lat1, lon1] = coords[i];
            const [lat2, lon2] = coords[i + 1];

            const R = 6371;
            const dLat = ((lat2 - lat1) * Math.PI) / 180;
            const dLon = ((lon2 - lon1) * Math.PI) / 180;

            const a =
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos((lat1 * Math.PI) / 180) *
                Math.cos((lat2 * Math.PI) / 180) *
                Math.sin(dLon / 2) *
                Math.sin(dLon / 2);

            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            total += R * c;
        }

        return total;
    }

    // backward-compat
    static calculateRouteLength(route: Route): number {
        return this.calculateRouteLengthKm(route);
    }

    /**
     * ✅ ВАЖНО:
     * containerPoints УЖЕ в правильном порядке (КП1 → КПN),
     * поэтому НЕ сортируем — используем порядок массива.
     */
    static createRouteFromContainerPoints(
        id: string,
        name: string,
        containerPoints: ContainerPoint[],
        style?: RouteStyle
    ): Route {
        if (!containerPoints || containerPoints.length < 2) {
            throw new Error("Для создания маршрута необходимо минимум 2 КП");
        }

        const routePoints: RoutePoint[] = containerPoints.map((kp, index) => ({
            coords: kp.coords,
            id: kp.id,
            name: kp.name || `КП ${index + 1}`,
            description: kp.address || kp.name || `Контейнерная площадка ${index + 1}`,
            order: index, // строго по позиции
        }));

        return {
            id,
            name,
            points: routePoints,
            style: style || this.getDefaultStyle(),
            visible: true,
            description: `Маршрут через ${containerPoints.length} КП`,
        };
    }
}
